import AsyncStorage from '@react-native-async-storage/async-storage';
import type { ReceiptWidthMm } from '@vuelto/print';
import {
  DEFAULT_BRANCH_RECEIPT_PRINT_CONFIG,
  type BranchReceiptPrintConfig,
  type ReceiptPrintMode,
} from '../domain/printConfig';

// Config por dispositivo (AsyncStorage local): dos cajas de la misma sucursal
// pueden tener modo/autoPrint distintos — no hay roaming entre dispositivos.
const STORAGE_KEY = '@vuelto/receipt_print_config_by_device';
const LEGACY_STORAGE_KEY = '@vuelto/receipt_print_config_by_branch';

type StoredMap = Record<string, Partial<BranchReceiptPrintConfig>>;

function parseStoredMap(raw: string | null): StoredMap {
  if (!raw) return {};
  const parsed = JSON.parse(raw) as unknown;
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
  return parsed as StoredMap;
}

async function readMap(): Promise<StoredMap> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    const map = parseStoredMap(raw);
    // Migración lazy de la key legacy (pre-fix): mergea, persiste en la key
    // nueva y descarta la legacy (fire-and-forget; el merge ya quedó en RAM).
    const legacyRaw = await AsyncStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw) {
      const merged = { ...parseStoredMap(legacyRaw), ...map };
      // Await: un setItem fire-and-forget podría pisar un writeMap posterior.
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(merged)).catch(() => {});
      void AsyncStorage.removeItem(LEGACY_STORAGE_KEY).catch(() => {});
      return merged;
    }
    return map;
  } catch {
    return {};
  }
}

async function writeMap(map: StoredMap): Promise<void> {
  // Propagar el error: un catch silencioso mostraba "Impresión actualizada"
  // aunque la config no se hubiera guardado.
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

function normalizeConfig(partial: Partial<BranchReceiptPrintConfig> | undefined): BranchReceiptPrintConfig {
  const base = DEFAULT_BRANCH_RECEIPT_PRINT_CONFIG;
  const mode: ReceiptPrintMode =
    partial?.mode === 'off' || partial?.mode === 'browser' || partial?.mode === 'agent'
      ? partial.mode
      : base.mode;
  const width: ReceiptWidthMm =
    partial?.widthMm === 58 || partial?.widthMm === 72 || partial?.widthMm === 80
      ? partial.widthMm
      : base.widthMm;
  return {
    mode,
    autoPrint: partial?.autoPrint === true,
    autoPrintComanda: partial?.autoPrintComanda === true,
    widthMm: width,
    agentUrl: partial?.agentUrl?.trim() || base.agentUrl,
    printerName: partial?.printerName?.trim() ?? '',
    kitchenPrinterName: partial?.kitchenPrinterName?.trim() ?? '',
  };
}

export async function getBranchReceiptPrintConfig(branchId: string): Promise<BranchReceiptPrintConfig> {
  if (!branchId.trim()) return DEFAULT_BRANCH_RECEIPT_PRINT_CONFIG;
  const map = await readMap();
  return normalizeConfig(map[branchId]);
}

export async function setBranchReceiptPrintConfig(
  branchId: string,
  patch: Partial<BranchReceiptPrintConfig>
): Promise<BranchReceiptPrintConfig> {
  if (!branchId.trim()) return DEFAULT_BRANCH_RECEIPT_PRINT_CONFIG;
  const map = await readMap();
  const next = normalizeConfig({ ...map[branchId], ...patch });
  map[branchId] = next;
  await writeMap(map);
  return next;
}
